import type { CurrentUser } from '@capsio/contracts';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, messageOf } from '../api-client.js';
import { AtmospherePanel } from '../components/AtmospherePanel.js';
import { BusyButton, Field, PasswordMeter } from '../components/Field.js';

/**
 * 接受邀请。
 *
 * 邀请邮件里的链接落在这儿——发信通道可用时，成员就是从这个页面第一次进系统的。
 * 原型只画到管理员那侧的「邀请邮件已发出」，但链接不能通向一个不存在的页面。
 */
export function InvitePage({ onSignedIn }: { onSignedIn: (user: CurrentUser) => void }): ReactNode {
  const { token = '' } = useParams<{ token: string }>();
  const [invite, setInvite] = useState<{ email: string; name: string } | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void api.readInvite(token).then(setInvite, (cause: unknown) => setLoadError(messageOf(cause)));
  }, [token]);

  async function accept(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (password.length < 12) {
      setError('密码至少 12 位');
      return;
    }
    if (confirm !== password) {
      setError('两次输入不一致');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await api.acceptInvite(token, password));
    } catch (cause) {
      setError(messageOf(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="split">
      <AtmospherePanel
        depth={4}
        kicker="接受邀请"
        statement={loadError === null ? '设一个密码就进来了' : '这个链接已经失效'}
        sub={
          loadError === null
            ? '这套系统没有注册入口，你是被管理员加进来的。'
            : '邀请链接 7 天内有效，且只能用一次。'
        }
      />

      <main className="form-side">
        <div className="col">
          {loadError !== null ? (
            <>
              <div className="head">
                <h1>链接失效了</h1>
                <p>{loadError}</p>
              </div>
              <div className="call brass">
                <span className="ic">!</span>
                <p>找管理员在「成员」里重新发一次邀请，或者让他给你一串一次性初始密码。</p>
              </div>
              <Link className="btn quiet" to="/login" style={{ textDecoration: 'none' }}>
                去登录页
              </Link>
            </>
          ) : invite === null ? (
            <p className="sub-hint">正在校验邀请链接…</p>
          ) : (
            <>
              <div className="head">
                <h1>{invite.name}，欢迎</h1>
                <p>
                  你的登录邮箱是 <strong>{invite.email}</strong>。设一个密码就可以进系统了。
                </p>
              </div>
              <form onSubmit={(event) => void accept(event)}>
                <Field
                  id="invite-pw"
                  label="密码"
                  aux="至少 12 位"
                  hint={<PasswordMeter value={password} />}
                >
                  <input
                    id="invite-pw"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </Field>
                <Field id="invite-pw2" label="再输一次" error={error}>
                  <input
                    id="invite-pw2"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••••••"
                    value={confirm}
                    onChange={(event) => setConfirm(event.target.value)}
                  />
                </Field>
                <BusyButton busy={busy} busyLabel="设置中" type="submit">
                  设置密码并进入
                </BusyButton>
              </form>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
